import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { autoTranslationQueryKeys, gTranslate } from '@lumapps/auto-translation';
import {
    ADVANCED_END_USER_TRANSLATION_FF,
    AUTO_TRANSLATION_HTML_ALLOWED_ATTRIBUTES,
    AUTO_TRANSLATION_HTML_ALLOWED_TAGS,
    GOOGLE_TRANSLATE_V2_MAX_LENGTH,
    GOOGLE_TRANSLATE_V3_MAX_LENGTH,
} from '@lumapps/auto-translation/constants';
import { useQueryClient } from '@lumapps/base-api/react-query';
import { getCurrentContentId } from '@lumapps/contents/ducks/selectors';
import { isFeatureEnabled } from '@lumapps/features';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { getInstanceColorPalette } from '@lumapps/style/ducks/selectors';
import { DEFAULT_LANGUAGE, GLOBAL } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { getConnectedUserLanguage } from '@lumapps/user/ducks/selectors';
import { sanitizeHTML } from '@lumapps/utils/string/sanitizeHtml';
import { WIDGET_CONTRIBUTION_TYPE } from '@lumapps/widget-contribution/constants';
import { WIDGET_HTML_TYPE } from '@lumapps/widget-html/constants';
import { WREX_VIEW_CLASSNAME } from '@lumapps/wrex/constants';
import { htmlValuesToSlate } from '@lumapps/wrex/serialization/slate/utils/htmlValuesToSlate';

import { queryKeys } from '../api';
import { CLIENT_COMPUTED_WIDGET_TYPE, EDITABLE_WIDGET_LIST } from '../constants';
import { getWidgetEntities } from '../ducks/selectors';
import { actions } from '../ducks/slice';
import { Widget, WidgetTranslatableData } from '../types';
import { parseWidgetsTranslatableDataToTranslatableString } from '../utils/parseWidgetsTranslatableDataToTranslatableString';

// Batched to avoid Google Translate API rate limits when translating to many languages at once.
const MAX_CONCURRENT_QUERIES = 5;

interface AutoTranslateParams {
    /** Override the widget data to translate for this specific call instead of using the hook's prop or Redux state */
    widgetsTranslatableData?: WidgetTranslatableData;
    /** Override the source language for this specific call */
    sourceLanguage?: string;
    /** Override the target languages for this specific call */
    targetLanguages?: string[];
}

interface UseWidgetsAutoTranslateReturn {
    /** Whether the autotranslate call had an error */
    hasError: boolean;
    /** Whether the autotranslate call is loading */
    isLoading: boolean;
    /** The translated widgets string */
    translatedValues?: Record<string, (string | undefined)[]>;
    /** Callback triggering the auto translate call. Returns a Promise that resolves when translation completes. */
    onAutoTranslate: (params?: AutoTranslateParams) => Promise<void>;
    /** Callback to set the init translation back  */
    onReset: () => void;
    /** Callback to clear the values like it was a page refresh  */
    onHardReset: () => void;
}

/**
 * Hook to auto-translate the given widgets text data (header, body and footer)
 * This hook handle the given text separation by chunk to not reach google translate character limit
 * It also correctly translate the widgets by batch to avoid reaching API Rate limit on too many target languages
 */
export const useWidgetsAutoTranslate = ({
    sourceLanguage: sourceLanguageProp,
    targetLanguages: targetLanguagesProp,
    widgetsTranslatableData,
    onTranslationUpdate,
}: {
    sourceLanguage?: string;
    targetLanguages?: string[];
    widgetsTranslatableData?: WidgetTranslatableData;
    onTranslationUpdate?: (translatedValues: Record<string, (string | undefined)[]>) => void;
} = {}): UseWidgetsAutoTranslateReturn => {
    const { error } = useNotification();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const widgetEntities = useSelector(getWidgetEntities);
    const currentLanguage = useSelector(currentLanguageSelector);
    const contentId = useSelector(getCurrentContentId);
    const userLanguage = useSelector(getConnectedUserLanguage);
    const colorPalette = useSelector(getInstanceColorPalette);
    const isUsingApiV3 = useSelector(isFeatureEnabled(ADVANCED_END_USER_TRANSLATION_FF));

    // Last successful translation result, keyed by target language. Stays undefined until a
    // translation completes without error, and is cleared by onHardReset.
    const [translatedValues, setTranslatedValues] = React.useState<Record<string, (string | undefined)[]> | undefined>(
        undefined,
    );
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);

    /** Non-translatable HTML (style/script tags) stripped before translation and re-appended after. */
    const nonTranslatableTextRef = React.useRef<Record<string, string>>({});

    const primaryTargetLang = userLanguage || currentLanguage || DEFAULT_LANGUAGE;

    const maxCharacters = isUsingApiV3 ? GOOGLE_TRANSLATE_V3_MAX_LENGTH : GOOGLE_TRANSLATE_V2_MAX_LENGTH;

    const getWidgetsData = React.useCallback(
        (override?: WidgetTranslatableData) => {
            const effectiveData = override ?? widgetsTranslatableData;
            if (effectiveData) {
                return { widgetsData: effectiveData, nonTranslatableText: {} as Record<string, string> };
            }
            let widgetsToTranslatableData: WidgetTranslatableData = {};
            let foundWidgetsNonTranslatableText: Record<string, string> = {};

            for (const value of Object.values(widgetEntities)) {
                const widgetHeaderValue = value.header?.label;
                const widgetFooterValue = value.footer?.label;
                let widgetBodyValue = (value.body as any)?.text;

                if (value.widgetType === WIDGET_HTML_TYPE) {
                    const htmlValue = (value.body as any)?.html;
                    const sanitizedHtmlValue = sanitizeHTML(htmlValue, {
                        allowedTags: AUTO_TRANSLATION_HTML_ALLOWED_TAGS,
                        allowedAttr: AUTO_TRANSLATION_HTML_ALLOWED_ATTRIBUTES,
                    });
                    widgetBodyValue = sanitizedHtmlValue;

                    if (!isEmpty(sanitizedHtmlValue)) {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(htmlValue, 'text/html');

                        // Extract all style and scripts tags
                        const nonTranslatableTags = [
                            ...Array.from(doc.querySelectorAll('style, script')).map((tag) => tag.outerHTML),
                        ].join('');

                        if (!isEmpty(nonTranslatableTags)) {
                            foundWidgetsNonTranslatableText = {
                                ...foundWidgetsNonTranslatableText,
                                [value.widgetId]: nonTranslatableTags,
                            };
                        }
                    }
                } else if (value.widgetType === WIDGET_CONTRIBUTION_TYPE) {
                    widgetBodyValue = document.querySelector(
                        `[data-id=widget-contribution-${value.widgetId}] .${WREX_VIEW_CLASSNAME}`,
                    )?.innerHTML;
                }

                // Only keep the widgets that have something to translate
                if (!isEmpty(widgetHeaderValue) || !isEmpty(widgetBodyValue) || !isEmpty(widgetFooterValue)) {
                    widgetsToTranslatableData = {
                        ...widgetsToTranslatableData,
                        [value.widgetId]: {
                            title: { valueToTranslate: widgetHeaderValue, shouldAutoTranslate: true },
                            body: { valueToTranslate: widgetBodyValue, shouldAutoTranslate: true },
                            footer: { valueToTranslate: widgetFooterValue, shouldAutoTranslate: true },
                            type:
                                value.widgetType === CLIENT_COMPUTED_WIDGET_TYPE
                                    ? value.body?.widgetType || value.widgetType
                                    : value.widgetType,
                        },
                    };
                    dispatch(
                        actions.setWidgetProperties({
                            widgetId: value.widgetId,
                            widgetProperties: { state: 'loading' },
                        }),
                    );
                }
            }

            return { widgetsData: widgetsToTranslatableData, nonTranslatableText: foundWidgetsNonTranslatableText };
        },
        [dispatch, widgetEntities, widgetsTranslatableData],
    );

    /** Concatenate the translatable values until they reach the max characters allowed */
    const getValuesByLimit = React.useCallback(
        (widgetsData: WidgetTranslatableData) => {
            const slicedValues = parseWidgetsTranslatableDataToTranslatableString(widgetsData);
            const valuesByLimit = [];
            let concatenedValue = '';

            for (const currentElement of slicedValues) {
                // Keep adding the value to the concatened string when possible
                if (currentElement.length + concatenedValue.length <= maxCharacters) {
                    concatenedValue += currentElement;
                }
                // When not possible anymore, this means we have a max translatable item
                // We then need to add it to the array
                else {
                    if (concatenedValue) {
                        valuesByLimit.push(concatenedValue);
                    }
                    concatenedValue = currentElement;
                }
            }

            // Used to handle last iteration that was added to concatenedValue but not pushed
            if (concatenedValue) {
                valuesByLimit.push(concatenedValue);
            }

            return valuesByLimit;
        },
        [maxCharacters],
    );

    /** Update the widget properties by using the translation */
    const updateWidgetsWithTranslations = React.useCallback(
        (widgetsHtmlTranslation: (string | undefined)[]) => {
            if (widgetsHtmlTranslation.length > 0) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(widgetsHtmlTranslation.join(''), 'text/html');
                const widgets = doc.querySelectorAll('article');

                widgets.forEach((widget) => {
                    const id = widget.dataset.widgetId;
                    const type = widget.dataset.widgetType || '';
                    if (!id) {
                        return;
                    }

                    const header = widget.querySelector('header');
                    const main = widget.querySelector('main');
                    const footer = widget.querySelector('footer');

                    let widgetTranslatedData: {
                        header?: Widget['header'];
                        footer?: Widget['footer'];
                        body?: any;
                    } = {};

                    if (header && header.textContent) {
                        widgetTranslatedData = { ...widgetTranslatedData, header: { label: header.textContent } };
                    }
                    if (main && main.textContent) {
                        let bodyValue;

                        if (type === WIDGET_CONTRIBUTION_TYPE) {
                            const htmlToSlate = htmlValuesToSlate({
                                slateHtml: main.innerHTML,
                                pasteOptions: { colorPalette },
                            });
                            bodyValue = { richText: { children: htmlToSlate } };
                        } else if (type === WIDGET_HTML_TYPE) {
                            const htmlNonTranslatableText = nonTranslatableTextRef.current[id];
                            bodyValue = htmlNonTranslatableText
                                ? { html: `${main.innerHTML}${htmlNonTranslatableText}` }
                                : { html: main.innerHTML };
                        } else {
                            bodyValue = { text: main.textContent };
                        }
                        widgetTranslatedData = { ...widgetTranslatedData, body: bodyValue };
                    }
                    if (footer && footer.textContent) {
                        widgetTranslatedData = { ...widgetTranslatedData, footer: { label: footer.textContent } };
                    }

                    const widgetQueryKey = queryKeys.getWidgetBlocks({
                        ownerResourceId: contentId,
                        ownerResourceType: 'content',
                        widgetType: type,
                        widgetId: id,
                        language: currentLanguage,
                    });

                    // We still rely on redux for the editable widgets as they doesn't use react query
                    if (EDITABLE_WIDGET_LIST.includes(type)) {
                        dispatch(
                            actions.setWidgetProperties({
                                widgetId: id,
                                widgetProperties: { state: 'loaded', ...widgetTranslatedData },
                            }),
                        );
                    } else {
                        queryClient.setQueryData(widgetQueryKey, (previousData?: { pages: any[] }) => {
                            const previousWidgetData = previousData?.pages?.[0];
                            const newWidgetData = {
                                ...(widgetTranslatedData?.header && {
                                    header: { ...previousWidgetData?.header, ...widgetTranslatedData.header },
                                }),
                                ...(widgetTranslatedData?.body && {
                                    body: { ...previousWidgetData?.body, ...widgetTranslatedData.body },
                                }),
                                ...(widgetTranslatedData?.footer && {
                                    footer: { ...previousWidgetData?.footer, ...widgetTranslatedData.footer },
                                }),
                            };

                            return {
                                ...previousData,
                                pages: [
                                    {
                                        ...previousWidgetData,
                                        state: 'loaded',
                                        ...newWidgetData,
                                    },
                                ],
                            };
                        });
                    }
                });
            }
        },
        [colorPalette, contentId, currentLanguage, dispatch, queryClient],
    );

    /** Apply a set of translations either through the consumer callback or directly onto the widgets. */
    const applyTranslations = React.useCallback(
        (values: Record<string, (string | undefined)[]>) => {
            if (onTranslationUpdate) {
                onTranslationUpdate(values);
            } else {
                for (const langValues of Object.values(values)) {
                    if (langValues) {
                        updateWidgetsWithTranslations(langValues);
                    }
                }
            }
        },
        [onTranslationUpdate, updateWidgetsWithTranslations],
    );

    const onAutoTranslate = React.useCallback(
        async (params?: AutoTranslateParams): Promise<void> => {
            // When explicit data is provided, always do a fresh translation (skip the re-use branch)
            // so that the override data is used even if stale translatedValues exist in the closure.
            if (!params && translatedValues && Object.keys(translatedValues).length > 0) {
                applyTranslations(translatedValues);
                return;
            }

            const sourceLanguage = params?.sourceLanguage ?? sourceLanguageProp;
            const targetLanguages = params?.targetLanguages?.length
                ? params.targetLanguages
                : targetLanguagesProp ?? [primaryTargetLang];

            setIsLoading(true);
            setHasError(false);
            try {
                const { widgetsData, nonTranslatableText } = getWidgetsData(params?.widgetsTranslatableData);
                nonTranslatableTextRef.current = nonTranslatableText;

                // Each entry is a concatenation of widget strings that stays within the API character limit.
                // Multiple entries = the content was too long and had to be split into chunks.
                const chunks = getValuesByLimit(widgetsData);
                if (chunks.length === 0) {
                    return;
                }

                // Translate by batch of languages to avoid reaching the API rate limit when there are
                // many target languages and/or chunks.
                const batchSize = Math.max(1, Math.floor(MAX_CONCURRENT_QUERIES / chunks.length));
                const result: Record<string, (string | undefined)[]> = {};
                for (let i = 0; i < targetLanguages.length; i += batchSize) {
                    const batchLangs = targetLanguages.slice(i, i + batchSize);
                    // eslint-disable-next-line no-await-in-loop
                    const batchResults = await Promise.all(
                        batchLangs.map(async (targetLang) => {
                            const translatedChunks = await Promise.all(
                                chunks.map((content) =>
                                    queryClient.fetchQuery({
                                        queryKey: autoTranslationQueryKeys.googleTranslate({
                                            content,
                                            targetLang,
                                            sourceLang: sourceLanguage,
                                            isContentHTML: true,
                                        }),
                                        queryFn: () =>
                                            gTranslate({
                                                content,
                                                targetLang,
                                                sourceLang: sourceLanguage,
                                                isContentHTML: true,
                                            }),
                                    }),
                                ),
                            );
                            return [targetLang, translatedChunks] as const;
                        }),
                    );
                    for (const [lang, translatedChunks] of batchResults) {
                        result[lang] = translatedChunks;
                    }
                }

                setTranslatedValues(result);
                applyTranslations(result);
            } catch (err) {
                setHasError(true);
                error({ translate: GLOBAL.GENERIC_ERROR });
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [
            translatedValues,
            sourceLanguageProp,
            targetLanguagesProp,
            primaryTargetLang,
            getWidgetsData,
            getValuesByLimit,
            queryClient,
            applyTranslations,
            error,
        ],
    );

    /** Reset to init translations */
    const onReset = React.useCallback(() => {
        if (translatedValues) {
            applyTranslations(translatedValues);
        }
    }, [translatedValues, applyTranslations]);

    /** Clear the values like it was a page refresh */
    const onHardReset = React.useCallback(() => {
        setTranslatedValues(undefined);
        setHasError(false);
        nonTranslatableTextRef.current = {};
    }, []);

    return {
        translatedValues,
        hasError,
        isLoading,
        onAutoTranslate,
        onReset,
        onHardReset,
    };
};
